<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import kebabCase from 'lodash-es/kebabCase';
  import { onMounted, ref, watch } from 'vue';
  import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

  import Badge from '../Badge/Badge.vue';
  import Menu from '../Menu/Menu.vue';
  import MenuItem from '../MenuItem/MenuItem.vue';
  import Tab, { type TabProps } from '../Tab/Tab.vue';
  import Tabs from '../Tabs/Tabs.vue';

  defineOptions({
    name: 'll-page-navigation',
  });

  export interface NavItem extends Omit<TabProps, 'value'> {
    /**
     * The tab's label
     */
    label: string;

    /**
     * The tab's label
     */
    value?: string;
  }

  export interface PageNavigationProps {
    /**
     * Index of active tab (zero-based)
     * @deprecated Use v-model instead.
     */
    activeIndex?: number | string;

    /**
     * The currently active tab value
     */
    modelValue?: string;

    /**
     * Array of tabs. A `tab` is an object containing a `label` and either an `href` | `to`
     */
    items: NavItem[];
  }

  const props = withDefaults(defineProps<PageNavigationProps>(), {
    activeIndex: undefined,
    modelValue: '',
  });

  const emit = defineEmits<{
    /**
     * Emits the current active nav value
     */
    (e: 'update:modelValue', activeTab: string): void;

    /**
     * Emits the current active nav index
     * @deprecated - Use v-model instead.
     */
    (e: 'change', activeNavIndex: number): void;
  }>();

  if (props.activeIndex) {
    logger.info('The `activeIndex` prop is deprecated. Use `v-model` instead.');
  }

  const route = useRoute();
  const router = useRouter();

  // this ref is needed to keep track of the active tab even if v-model is not passed
  const activeTab = ref('');

  function onUpdateActiveTab(newActiveTab: string) {
    if (newActiveTab === activeTab.value) return;

    activeTab.value = newActiveTab;
    emit('update:modelValue', newActiveTab);
  }

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (oldValue === newValue) return;

      // TODO: Remove this once activeIndex is removed
      const activeNavItemIndex = props.items.findIndex((item) => kebabCase(item.label) === newValue);
      emit('change', activeNavItemIndex);

      // Forcing updating activeTab when the component is in a keep alive state
      onUpdateActiveTab(newValue);
    },
  );

  onMounted(() => {
    if (!props.items.length) return;

    // TODO: Remove this once activeIndex is removed
    if (props.activeIndex !== undefined) {
      const activeTabItem = props.items[props.activeIndex];

      onUpdateActiveTab(kebabCase(activeTabItem.label));

      return;
    }

    if (props.modelValue !== activeTab.value) {
      onUpdateActiveTab(props.modelValue);
      return;
    }

    if (!router) return;

    const itemMatchingRoute = props.items.find((item) => {
      if (item?.disabled || (!item?.to && !item?.href)) return;

      const { path } = router.resolve((item?.to as RouteLocationRaw) || (item?.href as string));

      if (path === route.path) {
        return true;
      }

      if (!route.path.includes(path)) return;

      const sliceIndex = (route.path.length - route.path.lastIndexOf('/')) * -1;
      const parentPath = route.path.slice(0, sliceIndex);

      return path === parentPath;
    });

    if (!itemMatchingRoute) return;

    onUpdateActiveTab(itemMatchingRoute?.value || kebabCase(itemMatchingRoute.label));
  });
</script>

<template>
  <nav class="stash-page-navigation container" data-test="stash-page-navigation">
    <Tabs :active-tab="activeTab" @update:active-tab="onUpdateActiveTab">
      <Tab
        v-for="(item, index) in props.items"
        :key="item.label"
        :value="item?.value || kebabCase(item.label)"
        :to="item.to"
        :href="item.href"
        :badge="item.badge"
        :disabled="item.disabled"
        :data-id="index"
        :data-action-id="item?.value || kebabCase(item.label)"
      >
        {{ item.label }}
      </Tab>

      <template #more-actions>
        <Menu>
          <MenuItem
            v-for="item in props.items"
            :key="item.label"
            :data-action-id="item?.value || kebabCase(item.label)"
            :is-disabled="item.disabled"
          >
            <Badge v-if="item.badge" :content="item.badge" position="inline" color="red">
              {{ item.label }}
            </Badge>
            <template v-else>
              {{ item.label }}
            </template>
          </MenuItem>
        </Menu>
      </template>
    </Tabs>
  </nav>
</template>
